import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../services/api";
import { useAuthStore } from "../stores/authStore";
import { useFamilyStore } from "../stores/familyStore";
import ParentSidebar from "../components/ParentSidebar";
import BottomNav from "../components/BottomNav";
import { useTheme } from "../hooks/useTheme";
import type { Kid, Completion, Cashout } from "@starquest/core";
import styles from "./ParentHome.module.css";
import own from "./Explorers.module.css";

export default function StarBank() {
  const { familyId } = useAuthStore();
  const { kids, completions, cashouts, setKids, setCompletions, setCashouts } = useFamilyStore();
  const navigate = useNavigate();
  const theme = useTheme();
  const [reviewing, setReviewing] = useState<string | null>(null);

  useEffect(() => {
    if (!familyId) return;
    api.listKids(familyId).then((k) => setKids(k as Kid[]));
    api.listCompletions(familyId, "pending").then((c) => setCompletions(c as Completion[]));
    api.listCashouts(familyId).then((c) => setCashouts(c as Cashout[]));
  }, [familyId]);

  const pendingCashouts = cashouts.filter((c) => c.status === "pending");

  async function approveCashout(cid: string) {
    if (!familyId) return;
    setReviewing(cid);
    try {
      await api.reviewCashout(familyId, cid, "approve");
      api.listCashouts(familyId).then((c) => setCashouts(c as Cashout[]));
      api.listKids(familyId).then((k) => setKids(k as Kid[]));
    } catch { alert("Failed to approve cashout"); }
    finally { setReviewing(null); }
  }

  async function declineCashout(cid: string) {
    if (!familyId) return;
    setReviewing(cid);
    try {
      await api.reviewCashout(familyId, cid, "decline");
      api.listCashouts(familyId).then((c) => setCashouts(c as Cashout[]));
    } catch { alert("Failed to decline cashout"); }
    finally { setReviewing(null); }
  }

  function kidName(kidId: string) {
    return kids.find((k) => k.kidId === kidId)?.name ?? "Explorer";
  }
  function kidAvatar(kidId: string) {
    return kids.find((k) => k.kidId === kidId)?.avatar ?? "🧒";
  }

  return (
    <div className={styles.page}>
      <ParentSidebar pendingCount={completions.length} />
      <main className={styles.main}>
        <div className={styles.head}>
          <div>
            <h1 className={styles.h1}>🏦 {theme.pointBankTitle}</h1>
            <div className={styles.sub}>{theme.pointName} balances and cashout requests</div>
          </div>
          <button className={`${styles.btn} ${styles.ghost}`} onClick={() => navigate("/parent/points-value")}>
            ⚙️ Cash Rate Settings
          </button>
        </div>

        {pendingCashouts.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 15, marginBottom: 12, color: "rgba(240,240,255,0.8)" }}>
              ⏳ Pending Cashout Requests
            </div>
            {pendingCashouts.map((c) => (
              <div key={c.cashoutId} className={styles.verifRow} style={{ marginBottom: 8 }}>
                <div className={styles.verifAva} style={{ fontSize: 22 }}>{kidAvatar(c.kidId)}</div>
                <div className={styles.verifInfo}>
                  <div className={styles.verifName}>{kidName(c.kidId)} requested a cashout</div>
                  <div className={styles.verifMeta}>
                    {c.points} points → ${c.dollars.toFixed(2)} · {new Date(c.requestedAt).toLocaleDateString()}
                  </div>
                </div>
                <div className={styles.verifBtns}>
                  <button
                    className={`${styles.vbtn} ${styles.ok}`}
                    disabled={reviewing === c.cashoutId}
                    onClick={() => approveCashout(c.cashoutId)}
                  >
                    {reviewing === c.cashoutId ? "…" : "✓ Pay Out"}
                  </button>
                  <button
                    className={`${styles.vbtn} ${styles.no}`}
                    disabled={reviewing === c.cashoutId}
                    onClick={() => declineCashout(c.cashoutId)}
                  >
                    ✗
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 15, marginBottom: 12, color: "rgba(240,240,255,0.8)" }}>
          {theme.kidRole} Balances
        </div>
        <div className={own.grid}>
          {kids.length === 0 && <div className={own.empty}>No explorers yet</div>}
          {kids.map((kid) => (
            <div key={kid.kidId} className={own.card}>
              <div className={own.kidAva}>{kid.avatar}</div>
              <div className={own.kidName}>{kid.name}</div>
              <div className={own.kidLevel}>Level {kid.level}</div>
              <div className={own.kidStats}>
                <div className={own.stat}><span>{theme.pointEmoji}</span>{kid.pointBalance}</div>
                <div className={own.stat}><span>✨</span>{kid.xp} XP</div>
              </div>
            </div>
          ))}
        </div>
      </main>
      <BottomNav />
    </div>
  );
}
